import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ViewMode, SourceType } from '../../domain/types';
import { parseCapabilityRoute, routeForView } from '../../domain/capabilityNavigation';
import type { AxisFilterState } from '../../domain/multiAxisFiltering';

function readNavigationFromUrl(): {
  view: ViewMode;
  selectedId: string | null;
  modal: 'merge' | 'daily' | 'search' | null;
  searchTarget: string | null;
} {
  if (typeof window === 'undefined')
    return { view: 'radar', selectedId: null, modal: null, searchTarget: null };
  const params = new URLSearchParams(window.location.search);
  const view = parseCapabilityRoute(params.get('view')).view;
  const modalCandidate = params.get('modal');
  const modal =
    modalCandidate === 'merge' || modalCandidate === 'daily' || modalCandidate === 'search'
      ? modalCandidate
      : null;
  return {
    view,
    selectedId: params.get('item'),
    modal,
    searchTarget: modal === 'search' ? params.get('search') : null,
  };
}

function writeNavigationToUrl(
  view: ViewMode,
  selectedId: string | null,
  mode: 'push' | 'replace',
): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (view === 'radar') url.searchParams.delete('view');
  else url.searchParams.set('view', routeForView(view));
  if (selectedId) url.searchParams.set('item', selectedId);
  else url.searchParams.delete('item');
  window.history[`${mode}State`]({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export interface UiNavigationSliceOptions {
  onExpandItem?: (id: string) => void;
}

export function useUiNavigationSlice(options?: UiNavigationSliceOptions) {
  // Navigation & View
  const initialNavigation = useRef(readNavigationFromUrl()).current;
  const onExpandItemRef = useRef(options?.onExpandItem);
  onExpandItemRef.current = options?.onExpandItem;
  const [view, setViewState] = useState<ViewMode>(initialNavigation.view);
  const [selectedId, setSelectedIdState] = useState<string | null>(initialNavigation.selectedId);

  // Context (cross-source inspector) surface: whether the shared sidebar is
  // explicitly open, independent of which item is merely selected/focused.
  const [isContextSurfaceOpen, setIsContextSurfaceOpen] = useState<boolean>(false);

  // Radar work-context focus: which WorkThread a "Ver contexto" chip should
  // scroll to, expand, and focus once Radar mounts/updates. Consumed and
  // cleared by RadarView — see openspec/changes/provider-item-interaction-ux
  // (Work Context chips must focus the exact context, not just Radar root).
  const [focusedContextId, setFocusedContextId] = useState<string | null>(null);
  const navigateToContext = useCallback((contextId: string) => {
    setViewState('radar');
    setIsContextSurfaceOpen(false);
    setFocusedContextId(contextId);
    writeNavigationToUrl('radar', null, 'push');
  }, []);
  const clearFocusedContext = useCallback(() => {
    setFocusedContextId(null);
  }, []);

  const setView = useCallback((next: ViewMode) => {
    setViewState(next);
    setSelectedIdState(null);
    setIsContextSurfaceOpen(false);
    writeNavigationToUrl(next, null, 'replace');
  }, []);
  const selectItem = useCallback((id: string | null) => {
    setSelectedIdState(id);
    writeNavigationToUrl(readNavigationFromUrl().view, id, 'replace');
  }, []);
  const openContextSurface = useCallback((id: string) => {
    setSelectedIdState(id);
    setIsContextSurfaceOpen(true);
    writeNavigationToUrl(readNavigationFromUrl().view, id, 'replace');
  }, []);
  const closeContextSurface = useCallback(() => {
    setIsContextSurfaceOpen(false);
    setSelectedIdState(null);
    writeNavigationToUrl(readNavigationFromUrl().view, null, 'replace');
  }, []);

  // Per-view combinable filter state & groupings
  const [activeAxisFilters, setActiveAxisFiltersState] = useState<Record<string, AxisFilterState>>(
    {},
  );
  const [slackGrouping, setSlackGrouping] = useState<'flat' | 'channel' | 'task'>('flat');

  // Item Expansion
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // Modals & Search
  const [isMergeModalOpen, setIsMergeModalOpenState] = useState<boolean>(
    initialNavigation.modal === 'merge',
  );
  const [isDailyConfirmOpen, setIsDailyConfirmOpenState] = useState<boolean>(
    initialNavigation.modal === 'daily',
  );
  const [searchTarget, setSearchTargetState] = useState<string | null>(
    initialNavigation.searchTarget,
  );

  const setModalParam = useCallback(
    (modal: 'merge' | 'daily' | 'search' | null, search?: string | null) => {
      if (typeof window === 'undefined') return;
      const url = new URL(window.location.href);
      if (modal) url.searchParams.set('modal', modal);
      else url.searchParams.delete('modal');
      if (modal === 'search' && search) url.searchParams.set('search', search);
      else url.searchParams.delete('search');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    },
    [],
  );
  const setIsMergeModalOpen = useCallback(
    (open: boolean) => {
      setIsMergeModalOpenState(open);
      setModalParam(open ? 'merge' : null);
    },
    [setModalParam],
  );
  const setIsDailyConfirmOpen = useCallback(
    (open: boolean) => {
      setIsDailyConfirmOpenState(open);
      setModalParam(open ? 'daily' : null);
    },
    [setModalParam],
  );
  const setSearchTarget = useCallback(
    (target: string | null) => {
      setSearchTargetState(target);
      setModalParam(target ? 'search' : null, target);
    },
    [setModalParam],
  );

  useEffect(() => {
    const onPopState = () => {
      const next = readNavigationFromUrl();
      setViewState(next.view);
      setSelectedIdState(next.selectedId);
      setExpandedItems(next.selectedId ? { [next.selectedId]: true } : {});
      setIsMergeModalOpenState(next.modal === 'merge');
      setIsDailyConfirmOpenState(next.modal === 'daily');
      setSearchTargetState(next.modal === 'search' ? next.searchTarget : null);
      setIsContextSurfaceOpen(false);
      if (next.selectedId) onExpandItemRef.current?.(next.selectedId);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!initialNavigation.selectedId) return;
    setExpandedItems((prev) => ({ ...prev, [initialNavigation.selectedId as string]: true }));
    onExpandItemRef.current?.(initialNavigation.selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToItem = useCallback((newView: ViewMode | SourceType, id: string) => {
    // Gmail is its own workspace view (Correo) — it must not be redirected to
    // Radar, matching how every other source navigates. See
    // openspec/changes/provider-item-interaction-ux (Gmail navigation fix).
    setIsContextSurfaceOpen(false);
    setViewState(newView as ViewMode);
    setSelectedIdState(id);
    writeNavigationToUrl(newView as ViewMode, id, 'push');
    setExpandedItems((prev) => ({ ...prev, [id]: true }));
    onExpandItemRef.current?.(id);
  }, []);

  const setActiveAxisFilters = useCallback((viewKey: string, next: AxisFilterState) => {
    setActiveAxisFiltersState((prev) => ({
      ...prev,
      [viewKey]: next,
    }));
  }, []);

  const isItemExpanded = useCallback(
    (id: string) => {
      return !!expandedItems[id];
    },
    [expandedItems],
  );

  const toggleItemExpanded = useCallback((id: string) => {
    setExpandedItems((prev) => {
      const willOpen = !prev[id];
      if (willOpen && onExpandItemRef.current) {
        onExpandItemRef.current(id);
      }
      return { ...prev, [id]: !prev[id] };
    });
  }, []);

  return useMemo(
    () => ({
      view,
      setView,
      selectedId,
      selectItem,
      isContextSurfaceOpen,
      openContextSurface,
      closeContextSurface,
      focusedContextId,
      navigateToContext,
      clearFocusedContext,
      navigateToItem,
      activeAxisFilters,
      setActiveAxisFilters,
      slackGrouping,
      setSlackGrouping,
      expandedItems,
      setExpandedItems,
      isItemExpanded,
      toggleItemExpanded,
      isMergeModalOpen,
      setIsMergeModalOpen,
      isDailyConfirmOpen,
      setIsDailyConfirmOpen,
      searchTarget,
      setSearchTarget,
    }),
    [
      view,
      setView,
      selectedId,
      selectItem,
      isContextSurfaceOpen,
      openContextSurface,
      closeContextSurface,
      focusedContextId,
      navigateToContext,
      clearFocusedContext,
      navigateToItem,
      activeAxisFilters,
      setActiveAxisFilters,
      slackGrouping,
      expandedItems,
      isItemExpanded,
      toggleItemExpanded,
      isMergeModalOpen,
      setIsMergeModalOpen,
      isDailyConfirmOpen,
      setIsDailyConfirmOpen,
      searchTarget,
      setSearchTarget,
    ],
  );
}
